import { create, StateCreator } from 'zustand'
import {
  MemberType,
  Region,
  EducationalLevel,
  SortBy,
  SortOrder,
} from '@/types/contest'

export const contestSorting: {
  id: string
  value: SortBy
  label: string
  order: SortOrder
}[] = [
  {
    id: 'created_timestamp_desc',
    value: 'created_timestamp',
    label: 'วันประกาศ (มากไปน้อย)',
    order: 'desc',
  },
  {
    id: 'created_timestamp_asc',
    value: 'created_timestamp',
    label: 'วันประกาศ (น้อยไปมาก)',
    order: 'asc',
  },
  {
    id: 'closing_date_desc',
    value: 'closing_date',
    label: 'วันหมดเขตสมัคร (มากไปน้อย)',
    order: 'desc',
  },
  {
    id: 'closing_date_asc',
    value: 'closing_date',
    label: 'วันหมดเขตสมัคร (น้อยไปมาก)',
    order: 'asc',
  },
]

// Search Slice
export interface SearchSlice {
  query: string
  setQuery: (query: string) => void
}

export const createSearchSlice: StateCreator<
  ContestPreferenceStore,
  [],
  [],
  SearchSlice
> = (set) => ({
  query: '',
  setQuery: (query: string) => set({ query }),
})

// Filter Slice
export interface FilterSlice {
  categories: number[]
  educationalLevels: EducationalLevel[]
  regions: Region[]
  memberTypes: MemberType[]
  setCategories: (categories: number[]) => void
  setEducationalLevels: (educationalLevels: EducationalLevel[]) => void
  setRegions: (regions: Region[]) => void
  setMemberTypes: (memberTypes: MemberType[]) => void
}

export const createFilterSlice: StateCreator<
  ContestPreferenceStore,
  [],
  [],
  FilterSlice
> = (set) => ({
  categories: [],
  educationalLevels: [],
  regions: [],
  memberTypes: [],
  setCategories: (categories: number[]) => set({ categories }),
  setEducationalLevels: (educationalLevels: EducationalLevel[]) =>
    set({ educationalLevels }),
  setRegions: (regions: Region[]) => set({ regions }),
  setMemberTypes: (memberTypes: MemberType[]) => set({ memberTypes }),
})

// Sort Slice
export interface SortSlice {
  sortBy: SortBy
  sortOrder: SortOrder
  setSortBy: (sortBy: SortBy) => void
  setSortOrder: (sortOrder: SortOrder) => void
}

export const createSortSlice: StateCreator<
  ContestPreferenceStore,
  [],
  [],
  SortSlice
> = (set) => ({
  sortBy: contestSorting[0]?.value ?? 'created_timestamp',
  sortOrder: contestSorting[0]?.order ?? 'desc',
  setSortBy: (sortBy: SortBy) => set({ sortBy }),
  setSortOrder: (sortOrder: SortOrder) => set({ sortOrder }),
})

// Date Slice
export interface DateSlice {
  openDate: Date | null
  closingDate: Date | null
  setOpenDate: (openDate: Date | null) => void
  setClosingDate: (closingDate: Date | null) => void
}

export const createDateSlice: StateCreator<
  ContestPreferenceStore,
  [],
  [],
  DateSlice
> = (set) => ({
  openDate: null,
  closingDate: null,
  setOpenDate: (openDate: Date | null) => set({ openDate }),
  setClosingDate: (closingDate: Date | null) => set({ closingDate }),
})

// Team Slice
export interface TeamSlice {
  teamNumberMin: number | null
  teamNumberMax: number | null
  setTeamNumberMin: (teamNumberMin: number | null) => void
  setTeamNumberMax: (teamNumberMax: number | null) => void
}

export const createTeamSlice: StateCreator<
  ContestPreferenceStore,
  [],
  [],
  TeamSlice
> = (set) => ({
  teamNumberMin: null,
  teamNumberMax: null,
  setTeamNumberMin: (teamNumberMin: number | null) => set({ teamNumberMin }),
  setTeamNumberMax: (teamNumberMax: number | null) => set({ teamNumberMax }),
})

// Prize Slice
export interface PrizeSlice {
  prizeNumberMin: number | null
  prizeNumberMax: number | null
  isPrizeSliderActive: boolean
  setPrizeNumberMin: (prizeNumberMin: number | null) => void
  setPrizeNumberMax: (prizeNumberMax: number | null) => void
  setIsPrizeSliderActive: (isPrizeSliderActive: boolean) => void
}

export const createPrizeSlice: StateCreator<
  ContestPreferenceStore,
  [],
  [],
  PrizeSlice
> = (set) => ({
  prizeNumberMin: 0,
  prizeNumberMax: 500_000,
  isPrizeSliderActive: false,
  setPrizeNumberMin: (prizeNumberMin: number | null) => set({ prizeNumberMin }),
  setPrizeNumberMax: (prizeNumberMax: number | null) => set({ prizeNumberMax }),
  setIsPrizeSliderActive: (isPrizeSliderActive: boolean) =>
    set({ isPrizeSliderActive }),
})

// Benefits Slice
export interface BenefitsSlice {
  certificate: boolean
  trophy: boolean
  internship: boolean
  publishWork: boolean
  companyPartnership: boolean
  isBooleanOptionActive: boolean
  setCertificate: (certificate: boolean) => void
  setTrophy: (trophy: boolean) => void
  setInternship: (internship: boolean) => void
  setPublishWork: (publishWork: boolean) => void
  setCompanyPartnership: (companyPartnership: boolean) => void
  setIsBooleanOptionActive: (isBooleanOptionActive: boolean) => void
}

export const createBenefitsSlice: StateCreator<
  ContestPreferenceStore,
  [],
  [],
  BenefitsSlice
> = (set) => ({
  certificate: false,
  trophy: false,
  internship: false,
  publishWork: false,
  companyPartnership: false,
  isBooleanOptionActive: false,
  setCertificate: (certificate: boolean) => set({ certificate }),
  setTrophy: (trophy: boolean) => set({ trophy }),
  setInternship: (internship: boolean) => set({ internship }),
  setPublishWork: (publishWork: boolean) => set({ publishWork }),
  setCompanyPartnership: (companyPartnership: boolean) =>
    set({ companyPartnership }),
  setIsBooleanOptionActive: (isBooleanOptionActive: boolean) =>
    set({ isBooleanOptionActive }),
})

// View Options Slice
export interface ViewOptionsSlice {
  isViewActiveContests: boolean
  setIsViewActiveContests: (isViewActiveContests: boolean) => void
}

export const createViewOptionsSlice: StateCreator<
  ContestPreferenceStore,
  [],
  [],
  ViewOptionsSlice
> = (set) => ({
  isViewActiveContests: false,
  setIsViewActiveContests: (isViewActiveContests: boolean) =>
    set({ isViewActiveContests }),
})

export enum PreferenceMode {
  Explore = 'explore',
  ForYou = 'for_you',
}

export interface PreferenceModeSlice {
  preferenceMode: PreferenceMode
  setPreferenceMode: (mode: PreferenceMode) => void
}

export const createPreferenceModeSlice: StateCreator<
  ContestPreferenceStore,
  [],
  [],
  PreferenceModeSlice
> = (set) => ({
  preferenceMode: PreferenceMode.Explore,
  setPreferenceMode: (mode: PreferenceMode) => set({ preferenceMode: mode }),
})

// Global Actions Slice
export interface GlobalActionsSlice {
  setAll: (state: Partial<ContestPreferenceStore>) => void
  resetAll: () => void
  getState: () => ContestPreferenceStore
}

export const createGlobalActionsSlice: StateCreator<
  ContestPreferenceStore,
  [],
  [],
  GlobalActionsSlice
> = (set, get) => ({
  setAll: (state: Partial<ContestPreferenceStore>) => set(state),
  resetAll: () =>
    set({
      query: '',
      categories: [],
      educationalLevels: [],
      regions: [],
      memberTypes: [],
      sortBy: contestSorting[0]?.value ?? 'created_timestamp',
      sortOrder: contestSorting[0]?.order ?? 'desc',
      openDate: null,
      closingDate: null,
      teamNumberMin: null,
      teamNumberMax: null,
      prizeNumberMin: 0,
      prizeNumberMax: 500_000,
      certificate: false,
      trophy: false,
      internship: false,
      publishWork: false,
      companyPartnership: false,
      isPrizeSliderActive: false,
      isBooleanOptionActive: false,
      isViewActiveContests: true,
    }),
  getState: () => get(),
})

// Combined Store Type
export type ContestPreferenceStore = SearchSlice &
  FilterSlice &
  SortSlice &
  DateSlice &
  TeamSlice &
  PrizeSlice &
  BenefitsSlice &
  ViewOptionsSlice &
  PreferenceModeSlice &
  GlobalActionsSlice

// Create the combined stores using the pattern you specified
export const useContestPreferenceStore = create<ContestPreferenceStore>(
  (...a) => ({
    ...createSearchSlice(...a),
    ...createFilterSlice(...a),
    ...createSortSlice(...a),
    ...createDateSlice(...a),
    ...createTeamSlice(...a),
    ...createPrizeSlice(...a),
    ...createBenefitsSlice(...a),
    ...createViewOptionsSlice(...a),
    ...createGlobalActionsSlice(...a),
    ...createPreferenceModeSlice(...a),
  })
)
